import { useState, useEffect, useCallback } from 'react';
import type { CachedFund } from '@renewal/types';
import { getFunds, ApiRequestError } from '../api/client.js';

interface UseFundsOptions {
  /**
   * Every cached fund with its `selectable` flag, not only the funds new hours
   * may go on. The edit form needs it to name an entry's fund that has closed
   * since the entry was written.
   */
  includeAll?: boolean;
}

interface UseFundsResult {
  funds: CachedFund[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook for fetching cached funds for dropdown rendering. By default only funds
 * open to new hours (active, funding status ACTIVE) come back.
 */
export function useFunds(options: UseFundsOptions = {}): UseFundsResult {
  const includeAll = options.includeAll ?? false;
  const [funds, setFunds] = useState<CachedFund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFunds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFunds({ includeAll });
      setFunds(response.funds);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load funds');
      }
      // Non-blocking: fund selection is optional
      setFunds([]);
    } finally {
      setLoading(false);
    }
  }, [includeAll]);

  useEffect(() => {
    fetchFunds();
  }, [fetchFunds]);

  return { funds, loading, error, refetch: fetchFunds };
}
